import { ReactNode } from 'react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import Popover from '@mui/material/Popover';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useSupportListStyles } from './support-list.styles';
import { catalogTranslationRef } from '../../utils/translation';
import { navigateTo } from '../../utils/utils';

export interface SupportOption {
  title: string;
  url: string;
  type: 'email' | 'slack' | 'jira' | 'ticket' | 'generic' | 'githubIssues';
  icon: ReactNode;
}

interface SupportListProps {
  open: boolean;
  onClose: () => void;
  supportOptions: SupportOption[];
  anchorEl?: HTMLElement | null;
}

export const SupportList = ({
  open,
  onClose,
  supportOptions,
  anchorEl,
}: SupportListProps) => {
  const { t } = useTranslationRef(catalogTranslationRef);
  const classes = useSupportListStyles();

  const handleOptionClick = (url: string) => {
    navigateTo(url);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            marginTop: '-8px',
          },
        },
      }}
      classes={{ paper: classes.popoverPaper }}
    >
      <DialogContent className={classes.contentContainer}>
        <Box className="headerSection">
          <Typography variant="body1" className="title">
            {t('support.listTitle')}
          </Typography>
          <Typography variant="body1" className="description">
            {t('support.listDescription')}
          </Typography>
        </Box>
        <List className={classes.listContainer}>
          {supportOptions.length > 0 ? (
            supportOptions.map((option, index) => (
              <ListItem
                key={index}
                className={classes.listItem}
                onClick={() => handleOptionClick(option.url)}
              >
                <ListItemIcon className="listItemIcon">
                  {option.icon}
                </ListItemIcon>
                <ListItemText className="listItemText" primary={option.title} />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                className="listItemText"
                primary="No support options available"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Popover>
  );
};
